import { Component, OnInit, ViewChild } from '@angular/core';
import { FormControl, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import { AbilityService } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import { MbsPopupType, ModalComponent, Toast, ToastService } from 'mbs-ui-kit';
import { PermissionsEnum } from 'mbs-ui-shared';
import { ComputersFacade } from 'mbs-ui-shared@facades/computers.facade';
import Computer, { AgentType, convertAgentTypeToNeededCase } from 'mbs-ui-shared@models/Computer';
import { ComputerBasedModal } from 'mbs-ui-shared@models/ComputersModals';
import { of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'mbs-computers-uninstall-agent-modal',
  templateUrl: './computers-uninstall-agent-modal.component.html'
})
export class ComputersUninstallAgentModalComponent implements ComputerBasedModal, OnInit {
  public readonly AgentType = AgentType;
  public readonly alertType = MbsPopupType;
  public computer: Computer;
  public loading = false;

  public get isPluralText(): boolean {
    return this.computer?.apps?.length > 1;
  }

  public get title(): string {
    return (
      this.i18nPipe.transform(`shared:modals.uninstModal${this.isPluralText ? 'TitlePlural' : 'Title'}`, { format: 'title' }) +
      Computer.getComputerName(this.computer)
    );
  }

  public get agentListForm(): UntypedFormGroup {
    return this.uninstallAgentForm.get('agentList') as UntypedFormGroup;
  }

  @ViewChild(ModalComponent, { static: true }) private baseModal;

  public uninstallAgentForm: UntypedFormGroup = new UntypedFormGroup({
    agentList: new UntypedFormGroup({
      backup: new FormControl(false),
      rmm: new FormControl(false),
      ra: new FormControl(false)
    })
  });

  constructor(
    private facade: ComputersFacade,
    private toastService: ToastService,
    private i18nPipe: I18NextPipe,
    private ability: AbilityService
  ) {}

  ngOnInit(): void {
    this.showDeepInst && this.agentListForm.addControl('deepInst', new FormControl(false));

    this.agentListForm.addValidators([Validators.required, this.agentListValidator.bind(this)]);

    [AgentType.Backup, AgentType.RMM, AgentType.RA].forEach((agentType) => {
      !Computer.hasAgent(this.computer, agentType) && this.agentListForm.get(agentType).disable();
    });

    this.showDeepInst &&
      (!this.ability.can('read', PermissionsEnum.ShowDeepInstinct) || !Computer.hasAgent(this.computer, AgentType.DeepInst)) &&
      this.agentListForm.get('deepInst').disable();
  }

  get showDeepInst(): boolean {
    return this.ability.can('read', PermissionsEnum.ShowDeepInstinctProvider);
  }

  /*
   * Send computer data + AppsId for uninstall agent(s)
   */
  handleResolve(): void {
    const appsIds = Object.entries(this.uninstallAgentForm.get('agentList').value)
      .filter(([, value]) => !!value)
      .map(([key]) => key.toLowerCase());

    if (!appsIds) return;

    this.loading = true;

    this.facade
      .uninstallAgents(this.computer, appsIds)
      .pipe(
        catchError(() => {
          return of(false);
        }),
        finalize(() => (this.loading = false))
      )
      .subscribe((results) => {
        for (const result of results) {
          if (!result) continue;
          this.handleShowToast(result);
        }
        this.facade.loadComputerByHid({ hid: this.computer.hid, force: true });
        this.baseModal.close();
      });
  }

  /*
   * Validation for the presence of at least one agent
   * @param agentList
   * @private
   */
  private agentListValidator(agentList: UntypedFormGroup): ValidationErrors | null {
    const Backup = agentList.get('backup').value;
    const RMM = agentList.get('rmm').value;
    const RA = agentList.get('ra').value;
    const DeepInst = this.showDeepInst ? agentList.get('deepInst').value : null;

    if (Backup || RMM || RA || DeepInst) {
      return null;
    }

    return { agent: { message: this.i18nPipe.transform('shared:modals.uninstModalValidationError') } };
  }

  private handleShowToast(result: any): void {
    this.toastService.toast(
      new Toast({
        header: this.i18nPipe.transform('shared:modals.uninstModalUninstallAgent'),
        content: result.ok
          ? this.i18nPipe.transform('shared:modals.uninstModalStartedInfo', { param: convertAgentTypeToNeededCase(result.appId) })
          : `${result.errorDescription}`,
        type: result.ok ? MbsPopupType.success : MbsPopupType.danger,
        icon: true
      })
    );
  }
}
